import { useAuth0 } from "@auth0/auth0-react";
import { Suspense, lazy, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route } from "react-router-dom";
import { loadCart, setStoreRef } from "store/slices/cartSlice";
import {
  loadFavorites,
  setFavoriteStoreRef,
} from "store/slices/favoritesSlice";
import { fetchUserInfo, clearUserInfo } from "store/slices/userSlice";
import store from "store/store";

import { ProtectedRoute, ToastProvider, Loading } from "./common";
import { Header, Footer, CenteredContent, CartDrawer } from "./components";
import ProfileSetupGuard from "./components/guards/ProfileSetupGuard";
import { ListProductNoFilter } from "./components/product";
import useProfileSetup from "./hooks/useProfileSetup";
import { LandingPage } from "./pages";
// Lazy loaded components
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const ListProductPage = lazy(
  () => import("./components/product/ListProductPage")
);
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ProfileEditCard = lazy(
  () => import("./components/profile/ProfileEditCard")
);
const PurchaseHistory = lazy(() => import("./pages/PurchaseHistory"));
const Invoices = lazy(() => import("./pages/Invoices"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const OrderAgain = lazy(() => import("./pages/OrderAgain"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const MeetOurTeam = lazy(() => import("./pages/MeetOurTeam"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const JdiqRaffleWinners = lazy(() => import("./pages/JdiqRaffleWinners"));
const GiftCardProgramPage = lazy(() => import("./pages/GiftCardProgramPage"));
const OurPartners = lazy(() => import("./pages/OurPartners"));
const Q3CataloguePage = lazy(() => import("./pages/Q3CataloguePage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const BrandsPage = lazy(() => import("./pages/BrandsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HistoryOrderDetails = lazy(() => import("./pages/HistoryOrderDetails"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ClearancePage = lazy(() => import("./pages/ClearancePage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));

// ⬇️ NEW: legal pages
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsRefund = lazy(() => import("./pages/TermsRefund"));
const ReturnProcess = lazy(() => import("./pages/ReturnProcess"));
const ReturnDetails = lazy(() => import("./pages/ReturnDetails"));
const PickupProcess = lazy(() => import("./pages/PickupProcess"));

// Loading fallback component
const PageLoading = () => (
  <div className="flex justify-center items-center min-h-[400px]">
    <Loading text="Loading page..." />
  </div>
);

export default function App() {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();

  const favoriteIds = useSelector((s) => s.favorites.items);

  useProfileSetup();

  // Load cart and favorites from localStorage immediately
  useEffect(() => {
    dispatch(loadCart());
    dispatch(loadFavorites());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserInfo({ user, getAccessTokenSilently }));
      // Update store reference with token getter for cart and favorites sync
      setStoreRef(store, getAccessTokenSilently);
      setFavoriteStoreRef(store, getAccessTokenSilently);
    } else {
      dispatch(clearUserInfo());
      // Clear token getter when not authenticated
      setStoreRef(store, null);
      setFavoriteStoreRef(store, null);
    }
  }, [isAuthenticated, user, getAccessTokenSilently, dispatch]);

  // Use Redux favorites state as the primary source
  const favoritesCsv =
    favoriteIds && favoriteIds.length > 0 ? favoriteIds.join(",") : "";

  return (
    <ToastProvider>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow">
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route
                path="/"
                element={
                  // No CenteredContent: the reference HOME is full-bleed and every
                  // landing section owns its own max-w-container shell. Wrapping it
                  // capped the hero at 1536px + px-4 — the one-container rule again.
                  <LandingPage />
                }
              />

              <Route
                path="/product/:id"
                element={
                  <CenteredContent maxWidth={false}>
                    <ProductDetail />
                  </CenteredContent>
                }
              />
              <Route
                path="/products/by-class/:nameAndId"
                element={<ListProductPage by="class" />}
              />
              <Route
                path="/products/by-brand/:brandName"
                element={<ListProductPage by="brand" />}
              />
              <Route
                path="/products/by-name/:name"
                element={<ListProductPage by="name" />}
              />
              <Route
                path="/products/by-category/:categoryNameAndId"
                element={<ListProductPage by="category" />}
              />
              <Route path="/products" element={<ListProductPage by="all" />} />
              {/* Bare, like the ListProductPage routes: these pages own their own
                  max-w-container shell, so CenteredContent would double-wrap them. */}
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/brands" element={<BrandsPage />} />

              <Route path="/cart" element={<CartPage />} />
              <Route
                path="/faq"
                element={
                  <CenteredContent>
                    <FAQPage />
                  </CenteredContent>
                }
              />
              <Route
                path="/about"
                element={
                  <CenteredContent>
                    <AboutUs />
                  </CenteredContent>
                }
              />
              <Route
                path="/team"
                element={
                  <CenteredContent>
                    <MeetOurTeam />
                  </CenteredContent>
                }
              />
              <Route
                path="/contact"
                element={
                  <CenteredContent>
                    <ContactPage />
                  </CenteredContent>
                }
              />
              <Route
                path="/blog"
                element={
                  <CenteredContent>
                    <BlogPage />
                  </CenteredContent>
                }
              />

              {/* ---------- Promotions & Catalogues ---------- */}
              <Route
                path="/promotions"
                element={<ListProductPage by="promotion" />}
              />
              <Route
                path="/promotions/monthly-special"
                element={<ListProductPage by="category" />}
              />
              <Route
                path="/promotions/jdiq"
                element={
                  <CenteredContent>
                    <JdiqRaffleWinners />
                  </CenteredContent>
                }
              />
              <Route
                path="/promotions/gift-card"
                element={
                  <CenteredContent>
                    <GiftCardProgramPage />
                  </CenteredContent>
                }
              />
              <Route path="/clearance" element={<ClearancePage />} />
              <Route
                path="/blog/:slug"
                element={
                  <CenteredContent>
                    <BlogDetailPage />
                  </CenteredContent>
                }
              />
              <Route
                path="/partners"
                element={
                  <CenteredContent>
                    <OurPartners />
                  </CenteredContent>
                }
              />
              <Route
                path="/promotions/q3-catalogue"
                element={
                  <CenteredContent>
                    <Q3CataloguePage />
                  </CenteredContent>
                }
              />

              {/* ⬇️ NEW: Legal pages (no cart panel) */}
              <Route
                path="/privacy-policy"
                element={
                  <CenteredContent>
                    <PrivacyPolicy />
                  </CenteredContent>
                }
              />
              <Route
                path="/terms"
                element={
                  <CenteredContent>
                    <TermsRefund />
                  </CenteredContent>
                }
              />
              <Route
                path="/pickup"
                element={
                  <CenteredContent>
                    <PickupProcess />
                  </CenteredContent>
                }
              />

              {/* ---------- Protected routes ---------- */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/returns"
                  element={
                    <CenteredContent>
                      <ReturnProcess />
                    </CenteredContent>
                  }
                />
                <Route
                  path="/returns/:rmaId"
                  element={
                    <CenteredContent>
                      <ReturnDetails />
                    </CenteredContent>
                  }
                />
                <Route
                  path="/favorites"
                  element={
                    <CenteredContent maxWidth={false}>
                      <ListProductNoFilter
                        searchIds={favoritesCsv}
                        by="favoriteItems"
                      />
                    </CenteredContent>
                  }
                />
                {/* Bare like the cart page: CheckoutPage owns its own container-lg
                    shell now, so CenteredContent would double-wrap it. */}
                <Route
                  path="/checkout/*"
                  element={
                    <ProfileSetupGuard>
                      <CheckoutPage />
                    </ProfileSetupGuard>
                  }
                />
                <Route
                  path="/re-order"
                  element={
                    <ProfileSetupGuard>
                      <OrderAgain />
                    </ProfileSetupGuard>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <CenteredContent>
                      <ProfilePage />
                    </CenteredContent>
                  }
                />
                <Route
                  path="/profile/edit"
                  element={
                    <CenteredContent>
                      <ProfileEditCard />
                    </CenteredContent>
                  }
                />
                <Route
                  path="/profile/settings"
                  element={
                    <ProfileSetupGuard>
                      {/* Bare, like /purchase-history: owns the reference's
                          account container. */}
                      <AccountSettings />
                    </ProfileSetupGuard>
                  }
                />
                <Route
                  path="/purchase-history"
                  element={
                    <ProfileSetupGuard>
                      {/* Bare, like /categories: both order pages own the
                          reference's account container (max-w-container +
                          px-[var(--gutter)]) — CenteredContent's px-[5%]
                          would double the gutter. */}
                      <PurchaseHistory />
                    </ProfileSetupGuard>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <ProfileSetupGuard>
                      {/* Bare, like /purchase-history: owns the reference's
                          account container. */}
                      <Invoices />
                    </ProfileSetupGuard>
                  }
                />
                <Route
                  path="/profile/history/order/:transactionId"
                  element={
                    <ProfileSetupGuard>
                      <HistoryOrderDetails />
                    </ProfileSetupGuard>
                  }
                />
              </Route>

              <Route
                path="*"
                element={
                  <CenteredContent>
                    <NotFound />
                  </CenteredContent>
                }
              />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        {/* Always mounted — `.cart-morph` slides it in off `data-open`; a conditional
            render would skip the animation. Opened from the header's CartIndicator. */}
        <CartDrawer />
      </div>
    </ToastProvider>
  );
}
